import { AutoRecipeDefaultParameters } from "./AutoRecipeDefaultParameters";
import { PendingParameterChecks } from "./PendingParameterChecks";
import { Navigate } from "react-router";
import { useTenant } from "hooks/settings";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { FixedMixDefaultParameters } from "./FixedMixDefaultParameters";
import { useMemo, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { useTenantTranslation } from "hooks/formatters";
import { useTabNavigation } from "hooks/useTabNavigation";
import { MaterialLimitDefaultParameters } from "./MaterialLimitDefaultParameters";
import { TargetBasketsDefaultParameters } from "./TargetBasketDefaultParameters";
import {
  DefaultParametersContext,
  DefaultParameterTabsActions,
} from "./context";
import { MaterialPropertiesDefaultParameters } from "./MaterialPropertiesDefaultParameters";
import { SaveDefaultsDialog } from "./SaveDefaultsDialog";

export const DefaultParametersPage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const fixedMixDefaultsEnabled = useIsBehindFeatureFlag("fixed_mix_defaults");
  const [tabActions, setTabActions] =
    useState<DefaultParameterTabsActions | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMaterialLimitChanges, setHasMaterialLimitChanges] = useState(false);
  const contextValue = useMemo(
    () => ({
      setTabActions,
      hasMaterialLimitChanges,
      setHasMaterialLimitChanges,
    }),
    [hasMaterialLimitChanges]
  );

  const tabSpecs = useMemo(
    () => [
      { key: "baskets" },
      { key: "material-limits" },
      { key: "material-properties" },
      { key: "search-parameters" },
      ...(fixedMixDefaultsEnabled ? [{ key: "fixed-mixes" }] : []),
    ],
    [fixedMixDefaultsEnabled]
  );

  const { selectedTabKey } = useTabNavigation({
    tabSpecs,
    defaultTabKey: "baskets",
    basePath: "/parameters",
  });

  const basketSaveBlocked =
    selectedTabKey === "baskets" && hasMaterialLimitChanges;

  const tabPanels = useMemo(
    () => (
      <>
        <Box
          hidden={selectedTabKey !== "baskets"}
          sx={{
            display: selectedTabKey === "baskets" ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <TargetBasketsDefaultParameters
            isActive={selectedTabKey === "baskets"}
            setConfirmOpen={setConfirmOpen}
          />
        </Box>
        <Box
          hidden={selectedTabKey !== "material-limits"}
          sx={{
            display: selectedTabKey === "material-limits" ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <MaterialLimitDefaultParameters
            isActive={selectedTabKey === "material-limits"}
            setConfirmOpen={setConfirmOpen}
          />
        </Box>
        <Box
          hidden={selectedTabKey !== "material-properties"}
          sx={{
            display: selectedTabKey === "material-properties" ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <MaterialPropertiesDefaultParameters
            isActive={selectedTabKey === "material-properties"}
            setConfirmOpen={setConfirmOpen}
          />
        </Box>
        <Box
          hidden={selectedTabKey !== "search-parameters"}
          sx={{
            display: selectedTabKey === "search-parameters" ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <AutoRecipeDefaultParameters
            key={tenantName}
            isActive={selectedTabKey === "search-parameters"}
            setConfirmOpen={setConfirmOpen}
          />
        </Box>
        {fixedMixDefaultsEnabled ? (
          <Box
            hidden={selectedTabKey !== "fixed-mixes"}
            sx={{
              display: selectedTabKey === "fixed-mixes" ? "flex" : "none",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <FixedMixDefaultParameters
              isActive={selectedTabKey === "fixed-mixes"}
              setConfirmOpen={setConfirmOpen}
            />
          </Box>
        ) : null}
      </>
    ),
    [selectedTabKey, fixedMixDefaultsEnabled, tenantName]
  );

  if (selectedTabKey === "fixed-mixes" && !fixedMixDefaultsEnabled)
    return <Navigate to={`/${tenantName}/parameters/baskets`} replace />;

  return (
    <DefaultParametersContext value={contextValue}>
      <AccountPageLayout title={t("defaultParameters")}>
        <Stack gap={2} sx={{ flex: 1, minHeight: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body1">
              {t("manageDefaultParameters")}
            </Typography>
            <Stack direction="row" gap={1} alignItems="center">
              {tabActions?.isChecking ? <PendingParameterChecks /> : null}
              <Button
                variant="outlined"
                color="secondary"
                disabled={!tabActions?.hasChanges || tabActions.isSaving}
                onClick={() => {
                  tabActions?.onDiscard();
                }}
              >
                {t("discardChanges")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={
                  !tabActions?.hasChanges ||
                  tabActions.isSaving ||
                  basketSaveBlocked ||
                  tabActions.saveAvailability === "blocked"
                }
                onClick={() => {
                  setConfirmOpen(true);
                }}
                sx={{ minWidth: 140, fontWeight: 600 }}
              >
                {t("saveChanges")}
              </Button>
            </Stack>
          </Stack>
          {tabPanels}
        </Stack>
        <SaveDefaultsDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
          }}
          onConfirm={async () => {
            await tabActions?.onSave();
          }}
          isSaving={tabActions?.isSaving ?? false}
          isChecking={tabActions?.isChecking ?? false}
          disabled={
            !tabActions?.hasChanges ||
            basketSaveBlocked ||
            tabActions.saveAvailability === "blocked"
          }
          conflicts={tabActions?.conflicts ?? []}
        />
      </AccountPageLayout>
    </DefaultParametersContext>
  );
};
